/**
 * The owners field: the faces, and the one way to change them.
 *
 * Ownership is a set — a compliance lead and a business owner is the norm —
 * so this replaces the single-owner select inside the governance card rather
 * than sitting beside it. Two controls that both "assign an owner" is how a
 * form teaches people to distrust it.
 *
 * @module components/regulation/policy/PolicyOwnersField
 */
import { CheckIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { useOrganizationPeople, usePolicyOwners } from "../../../state/governance";
import { Button } from "../../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../../ui/menu";
import { PersonAvatars } from "../PersonAvatars";

export function PolicyOwnersField({
  organizationId,
  policyId,
  canManage,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
  readonly canManage: boolean;
}) {
  const { snapshot: ownerSnapshot, save } = usePolicyOwners(organizationId, policyId);
  const { snapshot: peopleSnapshot } = useOrganizationPeople(organizationId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const owners = ownerSnapshot?.people ?? [];
  const people = peopleSnapshot?.people ?? [];
  const ownerIds = new Set(owners.map((person) => person.userId));

  const toggle = async (userId: string) => {
    setBusy(true);
    setMessage(null);
    const next = ownerIds.has(userId)
      ? owners.filter((person) => person.userId !== userId).map((person) => person.userId)
      : [...owners.map((person) => person.userId), userId];
    setMessage(await save(next));
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground">Owners</span>
      <div className="flex h-9 items-center gap-3">
        <PersonAvatars people={owners} size="md" max={4} emptyLabel="Unassigned" />
        {owners.length > 0 ? (
          <span className="min-w-0 truncate text-sm text-foreground">
            {owners[0]?.name ?? owners[0]?.email}
            {owners.length > 1 ? ` +${owners.length - 1}` : ""}
          </span>
        ) : null}
        {canManage ? (
          <Menu>
            <MenuTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  className="ms-auto cursor-pointer text-xs"
                />
              }
            >
              {busy ? "Saving…" : owners.length === 0 ? "Assign" : "Change"}
            </MenuTrigger>
            <MenuPopup align="end" className="max-h-80 w-72 overflow-y-auto">
              {people.length === 0 ? (
                <MenuItem disabled>No members in this organization</MenuItem>
              ) : (
                people.map((person) => (
                  <MenuItem
                    key={person.userId}
                    closeOnClick={false}
                    onClick={() => void toggle(person.userId)}
                    className="flex items-center gap-2"
                  >
                    <CheckIcon
                      className={cn(
                        "size-3.5 shrink-0",
                        ownerIds.has(person.userId) ? "text-primary" : "text-transparent",
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {person.name ?? person.email}
                      {person.jobTitle ? (
                        <span className="text-muted-foreground"> · {person.jobTitle}</span>
                      ) : null}
                    </span>
                  </MenuItem>
                ))
              )}
            </MenuPopup>
          </Menu>
        ) : null}
      </div>
      {message !== null ? <p className="text-xs text-destructive">{message}</p> : null}
    </div>
  );
}
